/**
 * Daily 시드 — `daily_table` 과 `daily_row` 모양 그대로.
 *
 * 9월 1일부터 오늘(TODAY) 전날까지, Daily 를 쓰는 사람의 근무일마다 하루 한 행을 만든다. 사람이
 * 일일이 적기엔 많아 규칙으로 만들되 실제처럼 보이도록 엔티티를 따른다:
 *  - 그날 배치된(TEAMS) 프로젝트 중 진행 중인 단계가 있는 곳에서만 일한다(PM 인 곳에 더 많이)
 *  - 한 일 문구는 그날 그 프로젝트의 단계(data/projects.ts PHASE_PLANS)와 다가오는 마일스톤에서 온다
 *  - 줄의 계획 칸은 전 근무일 저녁에 써 둔 것이다(plan_saved_at)
 *  - 승인된 연차로 쉰 시간만큼 한 일이 줄고, 하루 연차면 하루가 아예 없다
 *  - 휴직, 퇴사한 사람은 마지막 근무일(LAST_WORKDAY) 뒤로 없다
 *
 * 「오늘」은 SEED_TODAY 와 실제 오늘 중 늦은 날이다(TODAY) — 시드가 끝난 뒤에 열어도 오늘 보드가
 * 비지 않게 그 사이 근무일도 지나간 하루로 채운다. 오늘은 사람마다 미제출·계획만·일부 완료·제출이
 * 섞이고, 그 다음 날에는 몇 사람의 내일 할 일만 있다. 고정 난수라 같은 날 열면 같다.
 *
 * 한 사람이 한 프로젝트에 작업(줄)을 둘 올리는 날도 있다 — 보드가 작업 단위라서다.
 *
 * 정규와 연장은 줄에 넣지 않는다 — 하루 합계와 standard_hours 에서 나온다.
 */
import { shiftDate, today, weekStartOf } from "@/lib/dates";
import type { Daily, DailyCategory, DailyRow } from "@/types";
import { leaveHoursOn } from "./attendance";
import { PM_OF, TEAMS, phaseNameOn, plannedLogNear, projects } from "./projects";
import { SEED_START, SEED_TODAY, at, isWorkday, nearestWorkday, rng, workdays } from "./seed";
import { LAST_WORKDAY, staff } from "./staff";

/** mock 테이블의 한 행 — 응답에는 없는 user_id 를 들고 있다. */
export interface MockDaily extends Daily {
  user_id: string;
}

/**
 * 분류 — 설치할 때 들어오는 기본 분류(backend `CATEGORY_SEED`: 사내 업무와 그 아래 셋, 기타)에, LIFETHINGS
 * 주간 업무의 묶음(설계 · 감리 · 규모검토·공모 · 그래픽 · 영상 · 외주)을 더한 상태 — 이관 스크립트가 만드는
 * 분류와 같다. 프로젝트 작업에도 분류를 붙인다.
 */
export const dailyCategories: DailyCategory[] = [
  { id: "cat-design", parent_id: null, name: "설계", sort_order: 1 },
  { id: "cat-supervision", parent_id: null, name: "감리", sort_order: 2 },
  { id: "cat-contest", parent_id: null, name: "규모검토·공모", sort_order: 3 },
  { id: "cat-graphic", parent_id: null, name: "그래픽", sort_order: 4 },
  { id: "cat-video", parent_id: null, name: "영상", sort_order: 5 },
  { id: "cat-outsource", parent_id: null, name: "외주", sort_order: 6 },
  { id: "cat-office", parent_id: null, name: "사내 업무", sort_order: 7 },
  { id: "cat-etc", parent_id: null, name: "기타", sort_order: 8 },
  { id: "cat-edu", parent_id: "cat-office", name: "교육", sort_order: 1 },
  { id: "cat-admin", parent_id: "cat-office", name: "관리", sort_order: 2 },
  { id: "cat-sales", parent_id: "cat-office", name: "영업과 제안", sort_order: 3 },
];

/**
 * 프로젝트 작업의 분류 — 그래픽 일은 문구로, 나머지는 그날의 단계로 고른다. 공모는 규모검토·공모다.
 */
function categoryOf(projectId: string, date: string, body: string): string {
  if (/사이니지|소책자|인포그래픽|브로셔|간판/.test(body)) return "cat-graphic";
  if (projectId === "p-contest") return "cat-contest";
  return phaseNameOn(projectId, date) === "감리" ? "cat-supervision" : "cat-design";
}

const STANDARD_HOURS = 8;

/** 시드의 오늘 — 실제 오늘이 더 늦으면 그날. */
const TODAY = today() > SEED_TODAY ? today() : SEED_TODAY;

/**
 * 프로젝트별 한 일 문구 — LIFETHINGS 주간 업무(2025~2026)에 그 프로젝트로 적힌 칸에서 골랐다. 여기 없는
 * 프로젝트이거나 반쯤은 아래 단계별 문구를 쓴다.
 */
const PROJECT_WORK: Record<string, string[]> = {
  "p-2508": [
    "VE 리스트 검토, 구조 협의",
    "재설계 킥오프(업체 견적, 일정 협의)",
    "타분야 협의, 도면 작성, 모델링",
    "실시설계(구조, 파사드, 자재)",
    "실시설계(x-ref, 파사드 협의, 구조 협의)",
    "현황 반영 도면 수정",
    "현장 작업 가능 범위 확인",
    "철거 감리, 현장 조사",
    "기획, 규모검토 인포그래픽",
    "소책자 제작",
  ],
  "p-2410": [
    "상징공간 기본설계, 외주 협의",
    "상징공간 기본설계, 추진경과보고 작성",
    "세종로주차장 기본설계 검토",
    "상징공간 구조변경 검토",
    "상징공간 납품도서 준비, 적산자료 대응",
    "세종로공원 기본설계, 발주처 협의",
    "세종로공원 내역 보완",
    "발주처 회의자료 대응",
    "외주 협의(토목, 측량)",
    "공정회의, 협력업체 협의",
  ],
  "p-2601": [
    "공공디자인 심의 준비",
    "서울시 디자인심의 검토",
    "공공디자인 심의자료 변경",
    "도면 작성, 구조 및 파사드 협의",
    "도면 작성, 심의도서 작성",
    "모델링 변경(기둥, 램프)",
    "조경업체 대응",
    "디자인 디벨롭, 모델링, 레벨 검토",
  ],
  "p-2506": [
    "티테라스·벤치 모델링, 배치도 제작",
    "난간·계단·매트 상세 위치 전달",
    "사이니지 가이드라인 제작",
    "사이니지 디테일 작성",
    "벤치 보완 및 도면 작성",
    "숲길 현장 미팅",
    "고문님 보고 준비",
    "견적 요청, 샘플 확인 일정 조율",
  ],
  "p-2604": [
    "기본도서 작성",
    "실시설계 사전 협의",
    "실시설계 1차 납품",
    "실시설계 납품 준비",
    "면적 축소, VE 협의",
    "사례 조사, VE 아이템 및 내역 협의",
    "입면, 문주, 로비 디자인",
    "렌더링 작업",
  ],
  "p-2511": [
    "중간설계(시스템, 세부 입면, 구조)",
    "허가 보완, 문화재 협의",
    "실시설계(입면, 시스템)",
    "실시설계, 허가 보완(장애인)",
    "설계설명서, 발주처 검토 협의",
    "VE 협의, 실시설계도서 최종 납품 준비",
    "기본설계, 철거 관리",
    "현장 실측",
  ],
  "p-2404": [
    "설계변경도서 작성",
    "설계변경 허가 협의",
    "착공신고 이후 대응",
    "허가 보완, 현장 대응",
    "외장재 및 디테일 협의",
    "감리보고서 보완",
    "증축분 평면 및 모델링 검토",
    "설계변경도서 작성, 조명 계획",
  ],
  "p-2503": [
    "실시설계 기준 도면 작성 및 전달",
    "실시설계(기준 단면 상세 계획)",
    "미디어스페이스 변경 대응",
    "실시설계(외벽 단면), 시공사 미팅",
    "홍보관 계획",
    "회장님 보고자료 준비",
    "철거 시공사 선정 지원",
    "고문님 로비 보고",
  ],
  "p-contest": [
    "법규 및 규모 검토",
    "사이트 모델링",
    "매스 계획",
    "평면 및 매스 계획",
    "평면, 입면, 지붕 계획",
    "레빗 도면 작업",
    "공모전 마감 — 다이어그램",
  ],
};

/** 단계 이름별 한 일 문구. 여기 없는 단계는 kickoff 를 쓴다. */
const WORK: Record<string, string[]> = {
  kickoff: ["착수 준비 자료 검토", "과업 지시서 검토", "대지 자료 수집", "착수 회의 준비"],
  기획설계: [
    "대지 현황 조사",
    "법규 검토(건폐율, 용적률, 높이 제한)",
    "매스 스터디 3안 작성",
    "건축주 요구사항 정리",
  ],
  기본설계: [
    "평면 계획 수정",
    "입면 스터디",
    "기본설계 도면 작성",
    "면적표 정리",
    "구조 방식 검토 회의",
    "발주처 보고 자료 작성",
  ],
  인허가: [
    "건축허가 도서 정리",
    "인허가 보완 요청 검토",
    "주차 계획 보완",
    "관계 부서 협의 자료 작성",
    "심의 자료 작성",
  ],
  실시설계: [
    "실시설계 상세도 작성",
    "창호 일람표 정리",
    "마감 상세 검토",
    "구조 도면 취합",
    "설비 도면 간섭 검토",
    "물량 산출 검토",
  ],
  설계변경: [
    "설계변경 도서 작성",
    "변경 내역서 정리",
    "발주처 변경 요청 반영",
    "변경 전후 비교표 작성",
  ],
  "산지 일시사용신고": [
    "벤치, 사이니지 위치 표기 도면 작성",
    "신고 도면 발주처 전달",
    "허가업체 질의 회신",
  ],
  "작품 제출": ["공모전 마감 — 도면", "공모전 마감 — 모델링 및 렌더", "디지털 패널 작성"],
  감리: [
    "현장 감리, 사진 기록",
    "시공 상세 검토 회신",
    "자재 승인 검토",
    "감리 일지 작성",
    "공정 회의 참석",
  ],
};

/** 한 일 끝에 붙는 구체적인 대상 — 같은 문구가 줄줄이 반복되지 않게. */
const DETAIL = [
  "1층 로비",
  "3층 코어",
  "지하 주차장",
  "옥상 조경",
  "외벽 마감",
  "계단실",
  "주출입구",
  "남측 입면",
  "기계실",
  "배치도",
];

/** 프로젝트에서 일하지 않는 사람의 하루 — 분류만 골라 적는다. */
const OFFICE_WORK: Record<string, { category: string; body: string[] }[]> = {
  "u-acc": [
    {
      category: "cat-admin",
      body: ["월말 결산 자료 정리", "용역비 청구서 발행", "경비 정산 검토", "4대 보험 신고"],
    },
    { category: "cat-office", body: ["사무용품 발주", "사내 행사 준비", "법인 차량 관리"] },
  ],
  변경민: [
    {
      category: "cat-admin",
      body: ["WorkThings 운영 점검", "역할 정리", "계약 서류 검토", "주간 회의 진행"],
    },
    { category: "cat-sales", body: ["제안서 검토", "신규 발주처 미팅 준비"] },
  ],
};

/** 오늘 늘 「일부 완료」인 사람 — 개발 중 로그인해 보는 계정들. */
const MIXED_TODAY = ["u-staff", "u-pm2", "변경민"];

/** 대표는 Daily 를 쓰지 않는다. 역할이 아니라 그 사람이라 id 로 가린다. */
const DAILY_WRITERS = staff.filter((u) => u.id !== "u-owner").map((u) => u.id);

/** 그날 그 사람이 소속된 진행 중 프로젝트 — PM 인 곳은 두 번 넣어 더 자주 뽑히게. */
function projectsOn(userId: string, date: string): string[] {
  return TEAMS.filter((t) => t.user_id === userId && t.from <= date && (!t.to || date <= t.to))
    .filter((t) => phaseNameOn(t.project_id, date) !== null)
    .flatMap((t) =>
      PM_OF.get(t.project_id) === userId ? [t.project_id, t.project_id] : [t.project_id]
    );
}

function workText(r: ReturnType<typeof rng>, projectId: string, date: string): string {
  const upcoming = plannedLogNear(projectId, date);
  if (upcoming && r.chance(0.5)) return `${upcoming} 준비`;
  const own = PROJECT_WORK[projectId];
  if (own && r.chance(0.6)) return r.pick(own);
  const phrase = r.pick(WORK[phaseNameOn(projectId, date) ?? ""] ?? WORK.kickoff);
  return r.chance(0.6) ? `${phrase} — ${r.pick(DETAIL)}` : phrase;
}

/** 총시간을 줄 수만큼 0.5 단위로 나눈다 — 앞 줄일수록 크다. */
function splitHours(r: ReturnType<typeof rng>, total: number, count: number): number[] {
  if (count === 1) return [total];
  const parts: number[] = [];
  let left = total;
  for (let i = 0; i < count - 1; i++) {
    const share = Math.max(0.5, Math.round(left * (0.5 + r.next() * 0.3) * 2) / 2);
    const h = Math.min(share, left - 0.5 * (count - 1 - i));
    parts.push(h);
    left -= h;
  }
  parts.push(left);
  return parts;
}

interface Target {
  project_id: string | null;
  category_id: string | null;
  body: string;
}

function targetsFor(r: ReturnType<typeof rng>, userId: string, date: string): Target[] {
  const office = OFFICE_WORK[userId];
  if (office) {
    const rows: Target[] = office
      .filter((_, i) => i === 0 || r.chance(0.4))
      .map((o) => ({ project_id: null, category_id: o.category, body: r.pick(o.body) }));
    // 프로젝트에 이름만 올린 관리자도 가끔은 그 프로젝트 일을 한다.
    const pool = projectsOn(userId, date);
    if (pool.length && r.chance(0.2))
      rows.push({ project_id: pool[0], category_id: null, body: "발주처 협의 동석" });
    return rows;
  }
  const pool = projectsOn(userId, date);
  if (!pool.length)
    return [{ project_id: null, category_id: "cat-office", body: "사내 업무 지원" }];
  const want = r.chance(0.4) ? 1 : r.chance(0.75) ? 2 : 3;
  const chosen: string[] = [];
  for (let i = 0; i < 10 && chosen.length < want; i++) {
    const p = r.pick(pool);
    if (!chosen.includes(p)) chosen.push(p);
  }
  const rows: Target[] = chosen.flatMap((p) => {
    const body = workText(r, p, date);
    // 열에 한 번쯤은 분류 없이 올린 작업도 섞인다.
    const first = {
      project_id: p,
      category_id: r.chance(0.85) ? categoryOf(p, date, body) : null,
      body,
    };
    // 하루에 프로젝트마다 한 줄이다. 한 프로젝트에서 작업을 둘 한 날은 한 줄에 두 줄로 적는다 —
    // 고른 프로젝트가 적을수록 잦다.
    if (!r.chance(chosen.length === 1 ? 0.6 : 0.3)) return [first];
    const second = workText(r, p, date);
    return second === first.body ? [first] : [{ ...first, body: `${body}\n${second}` }];
  });
  // 인턴은 가끔 교육을 받고, 사무소 영상 일을 거든다.
  if (userId === "u-st5" && r.chance(0.15))
    rows.push({ project_id: null, category_id: "cat-edu", body: "신입 BIM 교육" });
  else if (userId === "u-st5" && r.chance(0.08))
    rows.push({ project_id: null, category_id: "cat-video", body: "사무소 소개 영상 편집" });
  // 사무소 아카이브는 프로젝트 밖의 기타 일이다.
  if (userId === "u-st3" && r.chance(0.1))
    rows.push({ project_id: null, category_id: "cat-etc", body: "사내 건축 아카이브 정리" });
  return rows;
}

/**
 * 오늘 · 내일의 줄 — 비프로젝트 업무는 Daily 화면에서 「데일리 추가」로 직접 더해야 생긴다. 프로젝트 일이
 * 있는 사람의 오늘 · 내일에는 미리 넣어 두지 않는다. 사내 일만 하는 사람(관리자)은 그대로 둔다.
 */
const upcoming = (targets: Target[]) =>
  targets.some((t) => t.project_id) ? targets.filter((t) => t.project_id) : targets;

/** 계획의 예상 시간 — 하루 8시간을 줄 수만큼 0.5 단위로 나눈 값. */
const estimate = (count: number) => Math.max(0.5, Math.round((STANDARD_HOURS / count) * 2) / 2);

const row = (
  dailyId: string,
  position: number,
  target: Target,
  fields: Partial<DailyRow>
): DailyRow => ({
  id: `${dailyId}-r${position}`,
  position,
  project_id: target.project_id,
  project_name: projects.find((p) => p.id === target.project_id)?.name ?? null,
  category_id: target.category_id,
  category_name: dailyCategories.find((c) => c.id === target.category_id)?.name ?? null,
  plan_description: "",
  plan_hours: 0,
  plan_saved_at: null,
  plan_updated_at: null,
  description: "",
  actual_hours: 0,
  completed_at: null,
  ...fields,
});

const header = (userId: string, date: string, rows: DailyRow[]) => {
  const total = rows.reduce((sum, r) => sum + r.actual_hours, 0);
  return {
    id: `daily-${userId}-${date}`,
    user_id: userId,
    work_date: date,
    week_start: weekStartOf(date),
    standard_hours: STANDARD_HOURS,
    total_hours: total,
    regular_hours: Math.min(total, STANDARD_HOURS),
    overtime_hours: Math.max(0, total - STANDARD_HOURS),
    rows,
    notes: [],
    note: "",
  };
};

/** 지나간 하루 — 계획을 전날 저녁에 쓰고 그날 저녁에 제출했다. */
function pastDay(userId: string, date: string): MockDaily | null {
  const r = rng(`${userId}:${date}`);
  const leave = leaveHoursOn(userId, date);
  if (leave >= 8) return null;
  const targets = targetsFor(r, userId, date);
  const id = `daily-${userId}-${date}`;
  const planSavedAt = at(nearestWorkday(date, -1), "18:25");

  // 하루 3% 는 제출을 빠뜨린다 — 미제출이 화면에서 어떻게 보이는지.
  const missed = r.chance(0.03);
  const available = STANDARD_HOURS - leave;
  const nearDeadline = targets.some((t) => t.project_id && plannedLogNear(t.project_id, date));
  const overtime = nearDeadline && r.chance(0.6) ? 1 + r.int(3) : r.chance(0.12) ? 1 : 0;
  const hours = splitHours(r, available + overtime, targets.length);
  const rows = targets.map((target, i) => {
    // 줄의 대부분은 전날 계획한 것이다. 계획 없이 하게 된 일도 있다.
    const planned = r.chance(0.75);
    return row(id, i, target, {
      plan_description: planned ? target.body : "",
      plan_hours: planned ? estimate(targets.length) : 0,
      plan_saved_at: planned ? planSavedAt : null,
      description: missed ? "" : target.body,
      actual_hours: missed ? 0 : hours[i],
      // 작업마다 끝낸 때 제출한다 — 오후에 하나씩.
      completed_at: missed
        ? null
        : at(date, `${14 + Math.min(i, 4)}:${String(10 + r.int(45)).padStart(2, "0")}`),
    });
  });
  return header(userId, date, rows);
}

/**
 * 오늘 — 사람마다 단계가 다르다. 미제출(아무것도 없음), 계획만, 일부 완료, 모두 완료(제출).
 * 계획은 오늘 아침에 올렸고, 완료한 작업은 오후에 한 일과 시간을 붙였다.
 */
function todayDay(userId: string): MockDaily | null {
  const r = rng(`${userId}:${TODAY}:plan`);
  const roll = r.next();
  // 로그인해 볼 법한 사람은 늘 「일부 완료」 — 내 작업에 제출한 것과 아직 안 한 것이 섞이게.
  const stage = MIXED_TODAY.includes(userId)
    ? "partly"
    : roll < 0.15
      ? "none"
      : roll < 0.55
        ? "plan"
        : roll < 0.85
          ? "partly"
          : "done";
  if (stage === "none" || !isWorkday(TODAY) || leaveHoursOn(userId, TODAY) >= 8) return null;
  const targets = upcoming(targetsFor(r, userId, TODAY));
  const id = `daily-${userId}-${TODAY}`;
  const planSavedAt = at(TODAY, `09:${String(r.int(50)).padStart(2, "0")}`);
  const hours = splitHours(r, STANDARD_HOURS, targets.length);
  const rows = targets.map((target, i) => {
    // 일부 완료: 앞에서부터 번갈아 끝냈고, 마지막 작업은 늘 남아 있다.
    const done = stage === "done" || (stage === "partly" && i % 2 === 0 && i < targets.length - 1);
    return row(id, i, target, {
      plan_description: target.body,
      plan_hours: estimate(targets.length),
      plan_saved_at: planSavedAt,
      description: done ? target.body : "",
      actual_hours: done ? hours[i] : 0,
      completed_at: done
        ? at(TODAY, `${13 + Math.min(i, 4)}:${String(10 + r.int(45)).padStart(2, "0")}`)
        : null,
    });
  });
  return header(userId, TODAY, rows);
}

/** 내일 — 할 일만 미리 써 둔 하루. 계획 칸만 차 있다. */
function tomorrowDay(userId: string): MockDaily {
  const tomorrow = shiftDate(TODAY, 1);
  const r = rng(`${userId}:${tomorrow}:plan`);
  const targets = upcoming(targetsFor(r, userId, tomorrow));
  const id = `daily-${userId}-${tomorrow}`;
  const rows = targets.map((target, i) =>
    row(id, i, target, {
      plan_description: target.body,
      plan_hours: estimate(targets.length),
      plan_saved_at: at(TODAY, "18:10"),
    })
  );
  return header(userId, tomorrow, rows);
}

/** 내일 할 일까지 써 둔 사람 — 전원이 아니라 몇 사람만. */
const PLANS_AHEAD = ["u-staff", "u-pm", "변경민"];

export const dailies: MockDaily[] = DAILY_WRITERS.flatMap((userId) => {
  const last = LAST_WORKDAY[userId] ?? shiftDate(TODAY, -1);
  const past = workdays(SEED_START, last)
    .map((date) => pastDay(userId, date))
    .filter((daily): daily is MockDaily => daily !== null);
  if (LAST_WORKDAY[userId]) return past;
  const now = todayDay(userId);
  return [
    ...past,
    ...(now ? [now] : []),
    ...(PLANS_AHEAD.includes(userId) ? [tomorrowDay(userId)] : []),
  ];
});
